// Leanport: pure helpers for what a member may see or do when the deployment
// runs with MCPHUB_ADMIN_ONLY_CONFIG (the backend then answers 403 to every
// configuration change from a non-admin).

type UserLike = { isAdmin?: boolean } | null | undefined;

type GroupLike = { servers: (string | { name: string })[] };

type ServerLike = {
  name: string;
  enabled?: boolean;
  config?: { oauth?: { identityMode?: 'shared' | 'personal' } };
  oauth?: { connected?: boolean };
};

/** True when the user is a member and configuration is admin-only. */
export const isConfigLocked = (
  user: UserLike,
  switches: { adminOnlyConfig?: boolean } | undefined,
): boolean => Boolean(switches?.adminOnlyConfig && !user?.isAdmin);

/**
 * Groups with the number of their servers the member can use. `servers` is
 * the list the API already filtered to the member's visible servers; groups
 * without any of them are dropped.
 */
export const groupsWithUsableServers = <G extends GroupLike>(
  groups: G[],
  servers: ServerLike[],
): { group: G; usableServers: number }[] => {
  const usable = new Set(servers.filter((s) => s.enabled !== false).map((s) => s.name));
  return groups
    .map((group) => ({
      group,
      usableServers: group.servers.filter((s) =>
        usable.has(typeof s === 'string' ? s : s.name),
      ).length,
    }))
    .filter((entry) => entry.usableServers > 0);
};

/** Enabled servers with per-user OAuth the current user has not connected yet. */
export const serversNeedingPersonalOAuth = <S extends ServerLike>(servers: S[]): S[] =>
  servers.filter(
    (s) =>
      s.enabled !== false &&
      s.config?.oauth?.identityMode === 'personal' &&
      !s.oauth?.connected,
  );

const pad = (value: number): string => String(value).padStart(2, '0');

/** `DD.MM.YYYY HH:mm` in local time (DATEV connection-status requirement). */
export const formatConsentDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return (
    `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};
